<template>
	<div>
		<PageHeader
			:data="{
				name: 'Client Statistics',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Clients', to: '/clients' },
					{ name: 'Statistics', to: '' },
				],
			}"
		/>

		<div class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
							<div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
								<div>
									<h4 class="mb-0">Billing Snapshot</h4>
									<small class="text-muted">Active clients with their latest invoice</small>
								</div>
								<div class="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
									<input v-model.trim="searchTerm" type="text" class="form-control" placeholder="Search by name, county or state" />
									<button class="btn btn-outline-secondary" :disabled="loading" @click="fetchStatistics">
										<font-awesome-icon :icon="['fas', loading ? 'spinner' : 'sync']" :class="{ 'fa-spin': loading }" class="me-1" />
										Refresh
									</button>
								</div>
							</div>

			<div class="d-flex flex-column gap-2 mb-3">
				<div class="filters-toolbar" ref="filtersToolbar">
					<div
						v-for="filter in activeFilters"
						:key="filter.key"
						:class="['filter-chip', { 'filter-chip--date': filter.type === 'dateAfter' || filter.type === 'dateRange' }]"
					>
						<div class="filter-chip__body">
							<font-awesome-icon :icon="filter.icon || ['fas', 'calendar']" class="text-muted" />
							<span class="fw-semibold">{{ filter.label }}</span>
							<span class="filter-chip__operator">{{ filterOperatorLabel(filter) }}</span>
							<template v-if="filter.type === 'dateAfter'">
								<input
									type="date"
									class="form-control form-control-sm filter-chip__input"
									v-model="filter.value"
									@change="handleFilterValueChange(filter)"
								/>
							</template>
							<template v-else-if="filter.type === 'dateRange'">
								<div v-for="field in filter.rangeFields" :key="field.key" class="filter-chip__range">
									<span class="filter-chip__range-label">{{ field.label }}</span>
									<input
										type="date"
										class="form-control form-control-sm filter-chip__input"
										v-model="filter.value[field.key]"
										@change="handleFilterValueChange(filter)"
									/>
								</div>
							</template>
							<template v-else-if="filter.type === 'boolean'">
								<span class="fw-semibold text-secondary">{{ booleanFilterValueLabel(filter) }}</span>
							</template>
						</div>
						<button class="filter-chip__remove" type="button" @click="removeFilter(filter.key)">
							<font-awesome-icon :icon="['fas', 'times']" />
						</button>
					</div>
					<div class="filters-toolbar__controls">
						<button
							class="filter-trigger"
							type="button"
							ref="filtersButton"
							:aria-expanded="filtersMenuOpen"
							:disabled="!availableFilterOptions.length"
							@click="toggleFiltersMenu"
						>
							<font-awesome-icon :icon="['fas', 'filter']" />
							<span>Filter</span>
						</button>
						<div v-if="filtersMenuOpen" class="filters-menu" :style="filtersMenuStyles">
							<button
								v-for="option in availableFilterOptions"
								:key="option.key"
								type="button"
								class="filters-menu__item"
								@click="activateFilter(option.key)"
							>
								{{ option.label }}
							</button>
						</div>
					</div>
				</div>
			</div>

							<div v-if="error" class="alert alert-danger" role="alert">
								{{ error }}
							</div>

							<div v-if="loading" class="d-flex justify-content-center py-5">
								<div class="spinner-border" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
							</div>

							<div v-else>
								<div class="summary-grid mb-3">
									<div v-for="card in summaryCountCards" :key="card.label" class="summary-card">
										<div class="summary-card__label">{{ card.label }}</div>
										<div class="summary-card__value" :class="card.accent">{{ card.value }}</div>
									</div>
								</div>
								<div class="summary-grid summary-grid--amounts mb-3">
									<div v-for="card in summaryAmountCards" :key="card.label" class="summary-card">
										<div class="summary-card__label">{{ card.label }}</div>
										<div class="summary-card__value" :class="card.accent">
											{{ formatAmount(card.value, card.currency || defaultCurrency) }}
										</div>
									</div>
								</div>

									<div v-if="filteredClients.length" class="table-responsive">
										<div class="d-flex justify-content-end mb-2">
											<button class="btn btn-outline-secondary btn-sm" @click="exportTableToCsv" :disabled="!filteredClients.length">
												<font-awesome-icon :icon="['fas', 'file-export']" class="me-1" />
												Export CSV
											</button>
										</div>
										<table class="table table-hover align-middle">
										<thead>
											<tr>
												<th scope="col">Client</th>
											<th scope="col">Contract start</th>
											<th scope="col">Contract end</th>
											<th scope="col">Invoice</th>
												<th scope="col">Amount</th>
												<th scope="col">Status</th>
												<th scope="col">Remaining</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="client in filteredClients" :key="client.id">
												<td>
												<div class="fw-semibold">
													<router-link :to="clientEditRoute(client.id)" class="text-decoration-none">
														{{ client.name }}
													</router-link>
												</div>
												<small class="text-muted">ID: {{ client.id }}</small>
											</td>
											<td>{{ formatDate(client.contract_start_at) }}</td>
											<td :class="[{ 'text-danger fw-semibold': isContractExpired(client.contract_ends_at) }]">{{ formatDate(client.contract_ends_at) }}</td>
											<td>
												<div v-if="client.last_invoice">
													<div class="fw-semibold">#{{ client.last_invoice.invoice_number || client.last_invoice.invoice_id }}</div>
													<small class="text-muted">Issued {{ formatDate(client.last_invoice.generation_date || client.last_invoice.created_at) }}</small>
												</div>
												<span v-else class="text-muted">No invoices</span>
											</td>
											<td>
												<span v-if="client.last_invoice">{{ formatAmount(client.last_invoice.amount, client.last_invoice.currency_code) }}</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td>
												<span v-if="client.last_invoice" class="badge text-uppercase" :class="statusBadge(client.last_invoice.status)">
													{{ client.last_invoice.status || 'unknown' }}
												</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td>{{ formatRemainingInfo(client) }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div v-else class="text-center py-5 text-muted">No clients match your search.</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import restApi from '@/api/index'
import { handleErrors } from '@/actions/helpers'

export default {
	name: 'ClientStatistics',
	components: {
		PageHeader,
	},
	data() {
		return {
			clients: [],
			loading: false,
			error: '',
			searchTerm: '',
			filters: {
				hasInvoice: false,
				onlyMunicipality: false,
				onlyTownWeb: false,
				onlyNonTownWeb: false,
			},
			filterOptions: [
				{
					key: 'contractStartAfter',
					label: 'From beginning of last contract',
					type: 'dateAfter',
					paramKey: 'contract_start_from',
					icon: ['fas', 'calendar'],
				},
				{
					key: 'contractStartRange',
					label: 'From specific date till end of contract',
					type: 'dateRange',
					icon: ['fas', 'calendar-alt'],
					rangeFields: [
						{
							key: 'start',
							label: 'From',
							paramKey: 'contract_start_from',
						},
						{
							key: 'until',
							label: 'Remaining until',
							paramKey: 'remaining_reference_date',
						},
					],
				},
				{
					key: 'hasInvoice',
					label: 'Show only clients with invoices',
					type: 'boolean',
					stateKey: 'hasInvoice',
					icon: ['fas', 'file-invoice-dollar'],
				},
				{
					key: 'onlyMunicipality',
					label: 'Show only municipality clients',
					type: 'boolean',
					stateKey: 'onlyMunicipality',
					icon: ['fas', 'landmark'],
				},
				{
					key: 'onlyTownWeb',
					label: 'Show only Town Web clients',
					type: 'boolean',
					stateKey: 'onlyTownWeb',
					icon: ['fas', 'city'],
				},
				{
					key: 'onlyNonTownWeb',
					label: 'Show only non-Town Web clients',
					type: 'boolean',
					stateKey: 'onlyNonTownWeb',
					icon: ['fas', 'ban'],
				},
			],
			activeFilters: [],
			filtersMenuOpen: false,
			filtersMenuStyles: {},
		}
	},
	computed: {
		availableFilterOptions() {
			const activeKeys = this.activeFilters.map(filter => filter.key)
			return this.filterOptions.filter(option => !activeKeys.includes(option.key))
		},
		filteredClients() {
			let dataset = [...this.clients]

			if (this.filters.hasInvoice) {
				dataset = dataset.filter(client => !!client.last_invoice)
			}

			if (this.filters.onlyMunicipality) {
				dataset = dataset.filter(client => (client.type || '').toLowerCase() === 'municipality')
			}

			if (this.filters.onlyTownWeb) {
				dataset = dataset.filter(client => (client.tw_client || '').toLowerCase() === 'yes')
			}

			if (this.filters.onlyNonTownWeb) {
				dataset = dataset.filter(client => (client.tw_client || '').toLowerCase() === 'no')
			}

			if (this.searchTerm) {
				const term = this.searchTerm.toLowerCase()
				dataset = dataset.filter(client => {
					const name = (client.name || '').toLowerCase()
					const county = (client.county || '').toLowerCase()
					const state = (client.state || '').toLowerCase()
					return name.includes(term) || county.includes(term) || state.includes(term)
				})
			}

			return dataset
		},
		summaryStats() {
			const stats = {
				paid: 0,
				unpaid: 0,
				expired: 0,
				total: 0,
				missingData: 0,
				paidAmount: 0,
				remainingAmount: 0,
			}

			this.filteredClients.forEach(client => {
				const invoice = client.last_invoice
				const status = (invoice?.status || '').toLowerCase()
				if (!invoice) {
					stats.missingData += 1
				}
				if (status === 'paid') {
					stats.paid += 1
					const paidValue = Number(invoice?.amount)
					if (!Number.isNaN(paidValue)) {
						stats.paidAmount += paidValue
					}
				} else if (status === 'unpaid') {
					stats.unpaid += 1
				}

				if (this.isContractExpired(client.contract_ends_at)) {
					stats.expired += 1
				}

				if (client.remaining_amount !== null && client.remaining_amount !== undefined) {
					const remainingValue = Number(client.remaining_amount)
					if (!Number.isNaN(remainingValue)) {
						stats.remainingAmount += remainingValue
					}
				}
			})

			stats.total = this.filteredClients.length

			return stats
		},
		defaultCurrency() {
			const withCurrency = this.filteredClients.find(client => client.last_invoice?.currency_code)
			return withCurrency?.last_invoice?.currency_code || 'USD'
		},
		summaryCountCards() {
			return [
				{ label: 'Paid', value: this.summaryStats.paid, accent: 'text-success' },
				{ label: 'Unpaid', value: this.summaryStats.unpaid, accent: 'text-danger' },
				{ label: 'Expired contracts', value: this.summaryStats.expired, accent: 'text-warning' },
				{ label: 'Total clients', value: this.summaryStats.total, accent: 'text-info' },
				{ label: 'Missing data', value: this.summaryStats.missingData, accent: 'text-secondary' },
			]
		},
		summaryAmountCards() {
			return [
				{ label: 'Paid amount', value: this.summaryStats.paidAmount, accent: 'text-success' },
				{ label: 'Remaining amount', value: this.summaryStats.remainingAmount, accent: 'text-primary' },
			]
		},
	},
	created() {
		this.fetchStatistics()
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside)
		window.addEventListener('resize', this.refreshFiltersMenuPosition)
		window.addEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
		window.removeEventListener('resize', this.refreshFiltersMenuPosition)
		window.removeEventListener('scroll', this.refreshFiltersMenuPosition, true)
	},
	methods: {
		async fetchStatistics() {
			this.loading = true
			this.error = ''
			try {
				const params = this.getFilterParams()
				const response = await restApi.get('clients/statistics', { params })
				this.clients = response.data
			} catch (error) {
				this.error = handleErrors(error) || 'Unable to load client statistics at the moment.'
			} finally {
				this.loading = false
			}
		},
		activateFilter(key) {
			const option = this.filterOptions.find(filter => filter.key === key)
			const alreadyActive = this.activeFilters.some(filter => filter.key === key)
			if (!option || alreadyActive) {
				return
			}
			const newFilter = { ...option }
			if (option.type === 'dateAfter') {
				newFilter.value = ''
			}
			if (option.type === 'dateRange') {
				newFilter.value = {}
				option.rangeFields.forEach(field => {
					newFilter.value[field.key] = ''
				})
				newFilter.lastRangeSignature = null
			}
			if (option.type === 'boolean' && option.stateKey) {
				this.filters[option.stateKey] = true
			}
			this.activeFilters = [...this.activeFilters, newFilter]
			this.closeFiltersMenu()
		},
		handleFilterValueChange(filter) {
			if (filter.type === 'dateAfter') {
				this.fetchStatistics()
			}
			if (filter.type === 'dateRange') {
				const values = filter.rangeFields.map(field => filter.value[field.key] || '')
				const signature = values.join('|')
				const allFilled = filter.rangeFields.every(field => !!filter.value[field.key])
				if (allFilled && signature !== filter.lastRangeSignature) {
					filter.lastRangeSignature = signature
					this.fetchStatistics()
				} else if (!allFilled && filter.lastRangeSignature) {
					filter.lastRangeSignature = null
					this.fetchStatistics()
				}
			}
		},
		removeFilter(key) {
			const filterToRemove = this.activeFilters.find(filter => filter.key === key)
			this.activeFilters = this.activeFilters.filter(filter => filter.key !== key)
			if (!filterToRemove) {
				return
			}
			if (filterToRemove.type === 'dateAfter') {
				if (filterToRemove.value) {
					this.fetchStatistics()
				}
				return
			}
			if (filterToRemove.type === 'dateRange') {
				const hadValues = filterToRemove.rangeFields.some(field => !!filterToRemove.value[field.key])
				if (hadValues) {
					this.fetchStatistics()
				}
				return
			}
			if (filterToRemove.type === 'boolean' && filterToRemove.stateKey) {
				this.filters[filterToRemove.stateKey] = false
			}
		},
		getFilterParams() {
			const params = {}
			this.activeFilters.forEach(filter => {
				if (filter.type === 'dateAfter' && filter.value) {
					params[filter.paramKey] = filter.value
				}
				if (filter.type === 'dateRange' && filter.value) {
					const isComplete = filter.rangeFields.every(field => !!filter.value[field.key])
					if (!isComplete) {
						return
					}
					filter.rangeFields.forEach(field => {
						const fieldValue = filter.value[field.key]
						if (fieldValue) {
							params[field.paramKey] = fieldValue
						}
					})
				}
			})
			return params
		},
		filterOperatorLabel(filter) {
			if (filter.type === 'dateAfter') {
				return 'after'
			}
			if (filter.type === 'dateRange') {
				return 'range'
			}
			return 'is'
		},
		booleanFilterValueLabel(filter) {
			if (filter.stateKey && this.filters[filter.stateKey]) {
				return 'Enabled'
			}
			return 'Disabled'
		},
		toggleFiltersMenu() {
			if (!this.availableFilterOptions.length) {
				return
			}
			if (this.filtersMenuOpen) {
				this.closeFiltersMenu()
			} else {
				this.updateFiltersMenuPosition()
				this.filtersMenuOpen = true
			}
		},
		closeFiltersMenu() {
			this.filtersMenuOpen = false
		},
		handleClickOutside(event) {
			const toolbar = this.$refs.filtersToolbar
			if (!this.filtersMenuOpen || !toolbar) {
				return
			}
			if (!toolbar.contains(event.target)) {
				this.closeFiltersMenu()
			}
		},
		updateFiltersMenuPosition() {
			const button = this.$refs.filtersButton
			if (!button) {
				this.filtersMenuStyles = {}
				return
			}
			const rect = button.getBoundingClientRect()
			const top = rect.bottom + window.scrollY + 8
			const left = rect.left + window.scrollX
			const width = Math.max(rect.width, 320)
			this.filtersMenuStyles = {
				top: `${top}px`,
				left: `${left}px`,
				width: `${width}px`,
			}
		},
		refreshFiltersMenuPosition() {
			if (this.filtersMenuOpen) {
				this.updateFiltersMenuPosition()
			}
		},
		clientEditRoute(id) {
			return { name: 'clientsEdit', params: { id } }
		},
		formatDate(date) {
			if (!date) {
				return '—'
			}
			const parsed = new Date(date)
			if (Number.isNaN(parsed.valueOf())) {
				return date
			}
			return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},
		formatAmount(amount, currency) {
			if (amount === null || amount === undefined || amount === '') {
				return '—'
			}
			const value = Number(amount)
			if (Number.isNaN(value)) {
				return amount
			}
			const formatter = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency || 'USD',
				minimumFractionDigits: 2,
			})
			return formatter.format(value)
		},
		statusBadge(status) {
			const normalized = (status || '').toLowerCase()
			if (normalized === 'paid') {
				return 'bg-success'
			}
			if (normalized === 'unpaid') {
				return 'bg-danger'
			}
			if (normalized === 'sent' || normalized === 'viewed') {
				return 'bg-warning text-dark'
			}
			return 'bg-secondary'
		},
		isContractExpired(date) {
			if (!date) {
				return false
			}
			const parsed = new Date(date)
			if (Number.isNaN(parsed.valueOf())) {
				return false
			}
			const today = new Date()
			return parsed < today
		},
		formatRemainingInfo(client) {
			const years = client.remaining_payments
			if (years === null || years === undefined) {
				return '—'
			}

			const numericYears = Number(years)
			if (Number.isNaN(numericYears)) {
				return years
			}

			let output = `${numericYears} ${numericYears === 1 ? 'year' : 'years'}`

			if (client.remaining_amount !== null && client.remaining_amount !== undefined) {
				const currency = client.last_invoice?.currency_code || 'USD'
				output += ` (${this.formatAmount(client.remaining_amount, currency)})`
			}

			return output
		},
		exportTableToCsv() {
			if (!this.filteredClients.length) {
				return
			}

			const headers = ['Client', 'Contract start', 'Contract end', 'Invoice', 'Amount', 'Status', 'Remaining']
			const rows = this.filteredClients.map(client => {
				const invoice = client.last_invoice
				const issued = invoice ? this.formatDate(invoice.generation_date || invoice.created_at) : ''
				const invoiceLabel = invoice ? `#${invoice.invoice_number || invoice.invoice_id} (Issued ${issued})` : 'No invoices'
				const amountLabel = invoice ? this.formatAmount(invoice.amount, invoice.currency_code) : '—'
				const statusLabel = invoice?.status || '—'
				const remainingLabel = this.formatRemainingInfo(client)

				return [
					`${client.name} (ID: ${client.id})`,
					this.formatDate(client.contract_start_at),
					this.formatDate(client.contract_ends_at),
					invoiceLabel,
					amountLabel,
					statusLabel,
					remainingLabel,
				]
			})

			const csvContent = [headers, ...rows]
				.map(row => row.map(this.escapeCsvValue).join(','))
				.join('\n')

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
			const url = URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `client-statistics-${new Date().toISOString().split('T')[0]}.csv`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)
		},
		escapeCsvValue(value) {
			const stringValue = value === null || value === undefined ? '' : String(value)
			if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
				return `"${stringValue.replace(/"/g, '""')}"`
			}
			return stringValue
		},
	},
}
</script>

<style scoped>
.filters-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	align-items: center;
}

.filters-toolbar__controls {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	position: relative;
}

.filter-trigger {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.45rem 1.1rem;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background: #fff;
	font-weight: 600;
	font-size: 0.95rem;
	color: #4b5563;
	min-height: 44px;
	transition: box-shadow 0.2s ease;
}

.filter-trigger:disabled {
	opacity: 0.6;
	pointer-events: none;
}

.filters-menu {
	position: fixed;
	min-width: 320px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 0.75rem;
	box-shadow: 0 10px 30px rgba(17, 24, 39, 0.15);
	padding: 0.25rem;
	z-index: 1055;
}

.filters-menu__item {
	display: block;
	width: 100%;
	text-align: left;
	padding: 0.4rem 0.75rem;
	border: none;
	background: transparent;
	font-size: 0.9rem;
	border-radius: 0.5rem;
	color: #111827;
}

.filters-menu__item:hover {
	background-color: #f3f4f6;
}

.filter-chip {
	display: inline-flex;
	align-items: stretch;
	border: 1px solid #dcdfe3;
	border-radius: 999px;
	background-color: #fff;
	overflow: hidden;
	min-height: 44px;
}

.filter-chip__body {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.35rem 0.75rem;
	white-space: nowrap;
}

.filter-chip--date .filter-chip__body {
	gap: 0.4rem;
}

.filter-chip__range {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
}

.filter-chip__range-label {
	text-transform: uppercase;
	font-size: 0.7rem;
	color: #6b7280;
}

.filter-chip__input {
	width: 100px;
	min-width: 0;
	height: 32px;
	padding: 0.2rem 0.5rem;
	font-size: 0.85rem;
}

.filter-chip__remove {
	border: none;
	background: transparent;
	padding: 0.35rem 0.65rem;
	color: #6b7280;
	display: inline-flex;
	align-items: center;
}

.filter-chip__remove:hover {
	color: #b91c1c;
}

	.filter-chip__operator {
		text-transform: uppercase;
		font-size: 0.75rem;
	color: #525866;
	background-color: #f1f3f5;
	padding: 0.15rem 0.5rem;
	border-radius: 999px;
}

.summary-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 0.75rem;
}

.summary-grid--amounts {
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.summary-card {
	border: 1px solid #e5e7eb;
	border-radius: 0.65rem;
	padding: 1rem;
	text-align: center;
	background-color: #fff;
}

.summary-card__label {
	text-transform: uppercase;
	color: #9ca3af;
	font-size: 0.75rem;
	letter-spacing: 0.05em;
	margin-bottom: 0.25rem;
}

.summary-card__value {
	font-size: 1.5rem;
	font-weight: 600;
}
</style>
